/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import dayjs from "dayjs"

import {
  ReturnRefundWorklistItem,
  ReturnRefundWorklistRequestRow,
} from "ecommerce/queries/returnRefundApi"
import { isActionRequiredBucket } from "ecommerce/utils/returnRefundHelpers"

import { formatDateForDisplay } from "../Incidents/utils"

/**
 * Prefix for the positional identity a request falls back to when the response omits
 * `requestID`. Distinctive so such a row is recognisable in a React key warning.
 */
const MISSING_REQUEST_ID_PREFIX = "missing-request-id-"

/** Rendered when the worklist omits an optional display field. */
const EMPTY_CELL = "-"

/** One claimed item's cells, rendered as one line inside its request's row. */
export interface ReturnRefundRowItem {
  /** Unique within the row — `itemID` can be absent, so the index backstops it. */
  key: string
  itemName: string
  quantity?: number
  amount: string
  /**
   * This item's own status text (`requestStatusContent` on the item message), falling back
   * to the request-level text so a response that only populates the parent still shows
   * something rather than a blank status line.
   */
  statusContent?: string
  /**
   * Whether this item's status label is emphasised (red rather than grey).
   *
   * True only when the request needs merchant action *and* this item is one of the items
   * driving the request's status. In a mixed request — say one item awaiting inspection and
   * a sibling already refunded — only the item the request status reflects is emphasised, so
   * the merchant sees which line actually needs them.
   */
  highlightStatus: boolean
}

/**
 * One table row = one whole request, same granularity as the Orders tab's
 * `ReturnRequestRow` but with different columns: the claimed items stay visible as
 * stacked lines inside the item-level cells. One request = one clickable row, since
 * every item of a request opens the same detail drawer anyway.
 */
export interface ReturnRefundRequestRow {
  key: string
  requestID: string
  /**
   * Short order number, which doubles as the key the detail drawer is looked up by. Falls
   * back to `orderID` for the same reason `toReturnRequestRow` does.
   */
  orderID: string
  date: string
  items: ReturnRefundRowItem[]
}

/**
 * The request's date, taken from its earliest claimed item so every row in a group agrees.
 *
 * `submittedAt` is per item and **epoch milliseconds** — the same unit the worklist's
 * `submittedAtFrom`/`submittedAtTo` filters use. Passing it through `dayjs.unix()`, which
 * expects seconds, reads it as ~1.79 trillion seconds and renders the year 58596.
 */
const formatGroupDate = (items: ReturnRefundWorklistItem[]): string => {
  const earliest = items
    .map(item => item.submittedAt)
    .filter((submittedAt): submittedAt is number => submittedAt !== undefined)
    .sort((a, b) => a - b)[0]

  return earliest ? dayjs(earliest).format(formatDateForDisplay) : ""
}

/**
 * Maps a `MexListReturnRefundWorklist` response into one row per request.
 *
 * Status renders per item line: the item message carries its own `requestStatus` /
 * `requestStatusContent` (resolved by the server for that item alone), so siblings in one
 * request can disagree — one refunded, one still pending. An item-less request still gets a
 * placeholder item line so the row stays visible rather than silently dropping from the
 * worklist; with no item of its own, it wears the request-level status.
 */
export const toReturnRefundRequestRows = (
  requests: ReturnRefundWorklistRequestRow[] | undefined,
): ReturnRefundRequestRow[] =>
  (requests ?? []).map((request, requestIndex) => {
    const items = request.items ?? []
    const orderID = request.shortOrderNumber ?? request.orderID ?? ""
    /*
     * Never fall back to `orderID`: one order can carry several requests, so that would
     * give them a shared identity — colliding row keys, and a row click that opens the
     * wrong request's drawer. The positional fallback keeps them distinct if the response
     * omits `requestID`, which it is not expected to.
     */
    const requestID =
      request.requestID || `${MISSING_REQUEST_ID_PREFIX}${requestIndex}`
    // `badge.text` is the server's own rendering of the same status — a usable fallback when
    // `requestStatusContent` is absent. Its colours are ignored; the highlight decides those.
    const requestStatusContent =
      request.requestStatusContent ?? request.badge?.text
    const needsAction = isActionRequiredBucket(request.bucket)

    return {
      key: requestID,
      requestID,
      orderID,
      date: formatGroupDate(items),
      items: items.length
        ? items.map((item, index) => ({
            // Index-prefixed so it is unique by construction: `itemID ?? index` alone
            // collides when an itemID is a numeric string matching a sibling's fallback
            // (e.g. `itemID: "0"` next to an ID-less item at index 0).
            key: `${index}-${item.itemID ?? "missing"}`,
            itemName: item.itemName ?? EMPTY_CELL,
            quantity: item.quantity,
            amount: item.skuValueDisplay ?? EMPTY_CELL,
            statusContent: item.requestStatusContent ?? requestStatusContent,
            highlightStatus:
              needsAction &&
              item.requestStatus !== undefined &&
              item.requestStatus === request.requestStatus,
          }))
        : [
            {
              key: requestID,
              itemName: EMPTY_CELL,
              amount: EMPTY_CELL,
              statusContent: requestStatusContent,
              highlightStatus: needsAction,
            },
          ],
    }
  })

/**
 * A browser-time `YYYY-MM-DD` filter value as epoch milliseconds, which is what the worklist's
 * `submittedAtFrom` / `submittedAtTo` expect.
 *
 * `submittedAtTo` is **exclusive**, so the upper bound for an inclusive-looking date picker is
 * the start of the *following* day, not the end of the chosen one. `endOf("day")` yields
 * `23:59:59.999`, which under an exclusive comparison drops an item submitted in that final
 * millisecond from a range whose end date the merchant can see selected.
 */
export const toEpochMs = (
  browserDate: string,
  isEndOfDay: boolean = false,
): number | undefined => {
  if (!browserDate) {
    return undefined
  }

  const date = dayjs(browserDate)

  if (!date.isValid()) {
    return undefined
  }

  return (isEndOfDay
    ? date.add(1, "day").startOf("day")
    : date.startOf("day")
  ).valueOf()
}
